/**
 * Конфигурация роутера: определение всех маршрутов приложения с защитой через ProtectedRoute
 * Каждый защищенный роут проверяет авторизацию и permissions
 * Дата: 16.03.2026 — добавлен маршрут админского OKDoc tracking
 */
import { createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoginPage } from './pages/Login';
import { OkdocLandingPage } from '@/features/okdoc';
import { DashboardPage } from './pages/Dashboard';
import { MessagesPage } from './pages/Messages';
import { CampaignsPage } from './pages/Campaigns';
import { TemplatesPage } from './pages/Templates';
import { UsersPage } from './pages/Users';
import { SettingsPage } from './pages/Settings';
import { BotSettingsPage } from './pages/BotSettings';
import { AutoRepliesPage } from './pages/AutoReplies';
import { TelegramClientSetupPage } from './pages/TelegramClientSetup';
import { MessengerClientsSetupPage } from './pages/MessengerClientsSetup';
import { PhoneBindingPage } from './pages/PhoneBinding';
import { PatientFamiliesPage } from './pages/PatientFamilies';
import { AdminPage } from './pages/Admin';
import { ServicesPage } from './pages/Services';
import { AppointmentsPage } from './pages/Appointments';
import { InteractionsPage } from './pages/Interactions';
import { OkdocTrackingPage } from './pages/OkdocTracking';
import { OkdocLandingsPage } from './pages/OkdocLandings';
import { BroadcastQueuePage } from './pages/BroadcastQueue';
import { BroadcastSchedulesPage } from './pages/BroadcastSchedules';
import { ManualBroadcastsPage } from './pages/ManualBroadcasts';
import { BroadcastScheduleCreatePage } from './pages/BroadcastScheduleCreate';
import { ManualBroadcastCreatePage } from './pages/ManualBroadcastCreate';
import { ManualBroadcastHistoryPage } from './pages/ManualBroadcastHistory';
import { ManualBroadcastRecipientsPage } from './pages/ManualBroadcastRecipients';
import { AnalyticsPage } from './pages/Analytics';
import { PatientsPage } from './pages/Patients';
import { MediaLibraryPage } from './pages/MediaLibrary';
import { ChannelsDirectoryPage } from './pages/ChannelsDirectory';
import { AdminReplyPlaybooksPage } from './pages/AdminReplyPlaybooks';
import { BillingMonitorPage } from './pages/BillingMonitor';
import { HeartbeatPage } from './pages/Heartbeat';
import BlacklistPage from './pages/Blacklist';
import { NotFoundPage } from './pages/NotFound';

import { isDemoNotify as checkDemoNotify } from '@/demo/isDemoNotify';
// Demo mode: serve under /demo-notify/ when demo_notify_session cookie is present
const isDemoNotify = checkDemoNotify()

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/okdoc/:code',
    element: <OkdocLandingPage />,
  },
  {
    path: '/:publicCode',
    element: <OkdocLandingPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <DashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/messages',
    element: (
      <ProtectedRoute permission="messages:read">
        <MessagesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin-reply-playbooks',
    element: (
      <ProtectedRoute permission="messages:read">
        <AdminReplyPlaybooksPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/billing-monitor',
    element: (
      <ProtectedRoute>
        <BillingMonitorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/heartbeat',
    element: (
      <ProtectedRoute>
        <HeartbeatPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/appointments',
    element: (
      <ProtectedRoute permission="appointments:read">
        <AppointmentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/interactions',
    element: (
      <ProtectedRoute permission="appointments:read">
        <InteractionsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/okdoc-landings',
    element: (
      <ProtectedRoute permission="appointments:read">
        <OkdocLandingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/okdoc-tracking',
    element: (
      <ProtectedRoute permission="appointments:read">
        <OkdocTrackingPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/patients',
    element: (
      <ProtectedRoute permission="settings:read">
        <PatientsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/media-library',
    element: (
      <ProtectedRoute permission="appointments:read">
        <MediaLibraryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/analytics',
    element: (
      <ProtectedRoute permission="analytics:read">
        <AnalyticsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/channels-directory',
    element: (
      <ProtectedRoute permission="settings:read">
        <ChannelsDirectoryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/broadcast-queue',
    element: (
      <ProtectedRoute permission="campaigns:read">
        <BroadcastQueuePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/broadcast-schedules',
    element: (
      <ProtectedRoute permission="campaigns:read">
        <BroadcastSchedulesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/broadcast-schedules/create',
    element: (
      <ProtectedRoute permission="campaigns:write">
        <BroadcastScheduleCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/broadcast-schedules/:id/edit',
    element: (
      <ProtectedRoute permission="campaigns:write">
        <BroadcastScheduleCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/manual-broadcasts',
    element: (
      <ProtectedRoute permission="campaigns:read">
        <ManualBroadcastsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/manual-broadcasts/create',
    element: (
      <ProtectedRoute permission="campaigns:write">
        <ManualBroadcastCreatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/manual-broadcasts/history',
    element: (
      <ProtectedRoute permission="campaigns:read">
        <ManualBroadcastHistoryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/manual-broadcasts/:id/recipients',
    element: (
      <ProtectedRoute permission="campaigns:read">
        <ManualBroadcastRecipientsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/campaigns',
    element: (
      <ProtectedRoute permission="campaigns:read">
        <CampaignsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/templates',
    element: (
      <ProtectedRoute permission="templates:read">
        <TemplatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/users',
    element: (
      <ProtectedRoute permission="users:read">
        <UsersPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/bot-settings',
    element: (
      <ProtectedRoute permission="bot:read">
        <BotSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/auto-replies',
    element: (
      <ProtectedRoute permission="bot:read">
        <AutoRepliesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute permission="settings:read">
        <SettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/messenger-clients',
    element: (
      <ProtectedRoute anyOf={['settings:read', 'bot:read', 'messenger_clients:manage']}>
        <MessengerClientsSetupPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/telegram-client',
    element: (
      <ProtectedRoute anyOf={['settings:read', 'bot:read', 'messenger_clients:manage']}>
        <MessengerClientsSetupPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/telegram-client/advanced',
    element: (
      <ProtectedRoute anyOf={['settings:read', 'bot:read', 'messenger_clients:manage']}>
        <TelegramClientSetupPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/phone-binding',
    element: (
      <ProtectedRoute permission="settings:write">
        <PhoneBindingPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/families',
    element: (
      <ProtectedRoute permission="families:read">
        <PatientFamiliesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/services',
    element: (
      <ProtectedRoute permission="services:read">
        <ServicesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/blacklist',
    element: (
      <ProtectedRoute permission="settings:write">
        <BlacklistPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute>
        <AdminPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
], { basename: isDemoNotify ? "/demo-notify" : "/" });
